import * as questionnaires from '@seen/core/models/questionnaires/index'
import { ValidatedTool } from '@seen/core/models/validatedTool'
import { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import type { JSX } from 'react'
import { z } from 'zod'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormSubmitButton } from '#src/components/core/form/FormSubmitButton.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useValidatedToolForm } from '#src/components/validated-tool-form/shared/useValidatedToolForm.ts'
import {
  ValidatedToolInnerForm,
  type ValidatedToolInnerFormProps,
} from '#src/components/validated-tool-form/shared/ValidatedToolInnerForm.tsx'
import { ValidatedToolRadioGroupField } from '#src/components/validated-tool-form/shared/ValidatedToolRadioGroupField.tsx'
import { ValidatedToolResponseCard } from '#src/components/validated-tool-form/shared/ValidatedToolResponseCard.tsx'
import { ValidatedToolSection } from '#src/components/validated-tool-form/shared/ValidatedToolSection.tsx'
import type {
  ValidatedToolFieldSuggestion,
  ValidatedToolSetValue,
} from '#src/components/validated-tool-form/shared/validatedToolSuggestion.ts'
import type { UseFormReturn } from '#src/hooks/useForm.ts'

export interface ADLFormProps extends Omit<
  ValidatedToolInnerFormProps,
  'validatedTool' | 'form' | 'subtitle'
> {
  response?: ValidatedToolResponse
  /* AI-staged answers to pre-populate an unsubmitted form. */
  prefillValuesByLinkId?: Record<string, string>
  /* Per-field AI suggestion overlay, keyed by linkId. */
  renderFieldSuggestions?: (
    setValue: ValidatedToolSetValue
  ) => Record<string, ValidatedToolFieldSuggestion>
  restorationId?: string
}

const schema = z.object({
  bathing: z.enum(['independent', 'dependent']).nullable(),
  dressing: z.enum(['independent', 'dependent']).nullable(),
  toileting: z.enum(['independent', 'dependent']).nullable(),
  transferring: z.enum(['independent', 'dependent']).nullable(),
  continence: z.enum(['independent', 'dependent']).nullable(),
  feeding: z.enum(['independent', 'dependent']).nullable(),
  summary: z.string().nullable(),
})

const validatedTool = new ValidatedTool(questionnaires.adlQuestionnaire)

export function ADLForm({
  response,
  prefillValuesByLinkId,
  renderFieldSuggestions,
  restorationId,
  ...delegated
}: ADLFormProps): JSX.Element {
  const form = useValidatedToolForm({
    schema,
    validatedTool,
    response,
    prefillValuesByLinkId,
    restorationId,
  })
  const fieldSuggestions = renderFieldSuggestions?.(form.setSuggestionValue)

  return (
    <ValidatedToolInnerForm
      validatedTool={validatedTool}
      subtitle="For determining level of independence for activities of daily living"
      form={form}
      response={response}
      {...delegated}
    >
      {!!response && (
        <ValidatedToolResponseCard response={response}>
          <Score
            form={form}
            validatedTool={validatedTool}
            response={response}
          />
          <FormField
            control={form.control}
            name="summary"
            render={({ field: { value, ...rest } }) =>
              !value ? null : (
                <RichTextEditor
                  defaultValue={value}
                  variant="unstyled"
                  placeholder="Add summary…"
                  contentClassName="max-w-(--container-prose)"
                  {...rest}
                  disabled={false}
                  editable={false}
                />
              )
            }
          />
        </ValidatedToolResponseCard>
      )}
      <ValidatedToolSection title="Bathing">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="bathing"
          suggestion={fieldSuggestions?.bathing}
          options={[
            {
              value: 'independent',
              label: '(1) Independent',
              description:
                'Bathes self completely or needs help in bathing only a single part of the body such as the back, genital area or disabled extremity.',
            },
            {
              value: 'dependent',
              label: '(0) Dependent',
              description:
                'Needs help with bathing more than one part of the body, getting in or out of the tub or shower. Requires total bathing',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Dressing">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="dressing"
          suggestion={fieldSuggestions?.dressing}
          options={[
            {
              value: 'independent',
              label: '(1) Independent',
              description:
                'Gets clothes from closets and drawers and puts on clothes and outer garments complete with fasteners. May have help tying shoes.',
            },
            {
              value: 'dependent',
              label: '(0) Dependent',
              description:
                'Needs help with dressing self or needs to be completely dressed.',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Toileting">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="toileting"
          suggestion={fieldSuggestions?.toileting}
          options={[
            {
              value: 'independent',
              label: '(1) Independent',
              description:
                'Goes to toilet, gets on and off, arranges clothes, cleans genital area without help.',
            },
            {
              value: 'dependent',
              label: '(0) Dependent',
              description:
                'Needs help transferring to the toilet, cleaning self or uses bedpan or commode.',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Transferring">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="transferring"
          suggestion={fieldSuggestions?.transferring}
          options={[
            {
              value: 'independent',
              label: '(1) Independent',
              description:
                'Moves in and out of bed or chair unassisted. Mechanical transfer aids are acceptable',
            },
            {
              value: 'dependent',
              label: '(0) Dependent',
              description:
                'Needs help in moving from bed to chair or requires a complete transfer.',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Continence">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="continence"
          suggestion={fieldSuggestions?.continence}
          options={[
            {
              value: 'independent',
              label: '(1) Independent',
              description:
                'Exercises complete self control over urination and defecation.',
            },
            {
              value: 'dependent',
              label: '(0) Dependent',
              description:
                'Is partially or totally incontinent of bowel or bladder',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Feeding">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="feeding"
          suggestion={fieldSuggestions?.feeding}
          options={[
            {
              value: 'independent',
              label: '(1) Independent',
              description:
                'Gets food from plate into mouth without help. Preparation of food may be done by another person.',
            },
            {
              value: 'dependent',
              label: '(0) Dependent',
              description:
                'Needs partial or total help with feeding or requires parenteral feeding.',
            },
          ]}
        />
      </ValidatedToolSection>
      <Score form={form} validatedTool={validatedTool} response={response} />
      <ValidatedToolSection title="Summary">
        <FormField
          control={form.control}
          name="summary"
          render={({ field: { value, ...rest } }) => (
            <RichTextEditor
              defaultValue={value ?? ''}
              placeholder="Add summary…"
              contentClassName="max-w-(--container-prose)"
              {...rest}
            />
          )}
        />
      </ValidatedToolSection>
      {!response && (
        <div>
          <FormSubmitButton control={form.control}>Submit</FormSubmitButton>
        </div>
      )}
    </ValidatedToolInnerForm>
  )
}

function Score({
  form,
  validatedTool,
  response,
}: {
  form: UseFormReturn<any>
  validatedTool: ValidatedTool
  response?: ValidatedToolResponse
}): JSX.Element {
  const valuesByLinkId = form.watch()
  const result = response
    ? response
    : ValidatedToolResponse.buildSafe(validatedTool, { valuesByLinkId })
  return (
    <div className="flex items-center gap-2">
      <h2>
        Score: <b>{result.score}</b>
      </h2>
    </div>
  )
}
